import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ContactlessOutlinedIcon from "@mui/icons-material/ContactlessOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import "./credit-card-form.css";
import { attachOppwaCardPreviewSync } from "./oppwaCardPreviewSync";
import {
  setOppwaLayoutOptions,
  setOppwaPreviewHandlers,
} from "./oppwaPreviewBridge";
import {
  detectCardNetwork,
  formatCardNumberDisplay,
  type CardNetwork,
} from "./cardBrandUtils";
import CardNetworkLogo from "./CardNetworkLogo";

export type CardState = {
  number: string;
  holder: string;
  month: string;
  year: string;
  cvv: string;
};

export type CardValidity = {
  number: boolean;
  holder: boolean;
  month: boolean;
  year: boolean;
  cvv: boolean;
  allValid: boolean;
};

type WidgetStatus = "loading" | "ready" | "error";

type Props = {
  amount?: number;
  currency?: string;
  defaultNumber?: string;
  defaultHolder?: string;
  defaultMonth?: string;
  defaultYear?: string;
  defaultCVV?: string;
  showSubmit?: boolean;
  onChange?: (state: CardState, validity: CardValidity) => void;
  onSubmit?: (state: CardState, validity: CardValidity) => void;
  className?: string;
  cardBrandLabel?: string;
  widgetSlot?: React.ReactNode;
  onWidgetError?: () => void;
};

function clampDigits(value: string, maxLen: number) {
  return value.replace(/\D/g, "").slice(0, maxLen);
}

function formatNumberSpaces(num: string): string {
  return num.replace(/\s+/g, "").replace(/(\d{4})(?=\d)/g, "$1 ");
}

function truncateHolder(name: string, max = 22): string {
  const trimmed = name.trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max - 1)}…`;
}

const CreditCardForm: React.FC<Props> = ({
  amount,
  currency = "IQD",
  defaultNumber = "",
  defaultHolder = "",
  defaultMonth = "",
  defaultYear = "",
  defaultCVV = "",
  showSubmit = true,
  onChange,
  onSubmit,
  className = "",
  cardBrandLabel = "Al Hayat",
  widgetSlot,
  onWidgetError,
}) => {
  const embedWidget = Boolean(widgetSlot);
  const widgetRef = useRef<HTMLDivElement>(null);
  const previewDetachRef = useRef<(() => void) | null>(null);
  const loadTimeoutRef = useRef<number | null>(null);
  const widgetReadyRef = useRef(false);

  const [number, setNumber] = useState(clampDigits(defaultNumber, 19));
  const [holder, setHolder] = useState(defaultHolder.toUpperCase());
  const [month, setMonth] = useState(defaultMonth);
  const [year, setYear] = useState(defaultYear);
  const [cvv, setCVV] = useState(clampDigits(defaultCVV, 4));
  const [network, setNetwork] = useState<CardNetwork>("unknown");
  const [isCvvFocused, setIsCvvFocused] = useState(false);
  const [focusField, setFocusField] = useState<null | "number" | "holder" | "expire" | "cvv">(null);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [widgetStatus, setWidgetStatus] = useState<WidgetStatus>(embedWidget ? "loading" : "ready");
  const sceneRef = useRef<HTMLDivElement>(null);

  // Decorative pointer tilt for the card preview (UI only).
  const handleSceneMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (reducedMotion || event.pointerType !== "mouse") return;
      const el = sceneRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const px = (event.clientX - rect.left) / rect.width - 0.5;
      const py = (event.clientY - rect.top) / rect.height - 0.5;
      el.style.setProperty("--ccf-tilt-x", `${(-py * 10).toFixed(2)}deg`);
      el.style.setProperty("--ccf-tilt-y", `${(px * 14).toFixed(2)}deg`);
      el.style.setProperty("--ccf-glare-x", `${((px + 0.5) * 100).toFixed(1)}%`);
      el.style.setProperty("--ccf-glare-y", `${((py + 0.5) * 100).toFixed(1)}%`);
    },
    [reducedMotion]
  );

  const handleSceneLeave = useCallback(() => {
    const el = sceneRef.current;
    if (!el) return;
    el.style.setProperty("--ccf-tilt-x", "0deg");
    el.style.setProperty("--ccf-tilt-y", "0deg");
    el.style.setProperty("--ccf-glare-x", "30%");
    el.style.setProperty("--ccf-glare-y", "20%");
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    setOppwaLayoutOptions({ amount, currency });
  }, [amount, currency]);

  const handleFocus = useCallback((field: null | "number" | "holder" | "expire" | "cvv") => {
    setFocusField(field);
    setIsCvvFocused(field === "cvv");
  }, []);

  const attachPreview = useCallback((container: HTMLElement) => {
    previewDetachRef.current?.();
    previewDetachRef.current = attachOppwaCardPreviewSync(container, {
      onNumber: setNumber,
      onHolder: (value) => setHolder(value.toUpperCase()),
      onMonth: setMonth,
      onYear: setYear,
      onCvv: setCVV,
      onFocus: handleFocus,
      onBrand: setNetwork,
    });
  }, [handleFocus]);

  const markWidgetReady = useCallback(
    (container: HTMLElement) => {
      if (widgetReadyRef.current) return;
      widgetReadyRef.current = true;

      if (loadTimeoutRef.current) {
        window.clearTimeout(loadTimeoutRef.current);
        loadTimeoutRef.current = null;
      }

      attachPreview(container);
      setWidgetStatus("ready");
    },
    [attachPreview]
  );

  useEffect(() => {
    if (!embedWidget) return;

    setWidgetStatus("loading");
    widgetReadyRef.current = false;
    if (widgetRef.current) delete widgetRef.current.dataset.oppwaFinalized;

    loadTimeoutRef.current = window.setTimeout(() => {
      setWidgetStatus((prev) => {
        if (prev === "loading") {
          onWidgetError?.();
          return "error";
        }
        return prev;
      });
    }, 15000);

    setOppwaPreviewHandlers({
      onNumber: setNumber,
      onHolder: (value) => setHolder(value.toUpperCase()),
      onMonth: setMonth,
      onYear: setYear,
      onCvv: setCVV,
      onFocus: handleFocus,
      onBrand: setNetwork,
      onWidgetReady: markWidgetReady,
    });

    return () => {
      if (loadTimeoutRef.current) window.clearTimeout(loadTimeoutRef.current);
      previewDetachRef.current?.();
      previewDetachRef.current = null;
      setOppwaPreviewHandlers(null);
    };
  }, [embedWidget, handleFocus, markWidgetReady, onWidgetError]);

  const validity: CardValidity = useMemo(() => {
    const numberValid = number.replace(/\D/g, "").length >= 13;
    const holderValid = holder.trim().length >= 2;
    const monthValid = !!month && +month >= 1 && +month <= 12;
    const yearValid = !!year && +year >= new Date().getFullYear();
    const cvvValid = /^[\d•]{3,4}$/.test(cvv);
    return {
      number: numberValid,
      holder: holderValid,
      month: monthValid,
      year: yearValid,
      cvv: cvvValid,
      allValid: numberValid && holderValid && monthValid && yearValid && cvvValid,
    };
  }, [number, holder, month, year, cvv]);

  useEffect(() => {
    if (embedWidget) return;
    setNetwork(detectCardNetwork(number));
  }, [number, embedWidget]);

  useEffect(() => {
    if (embedWidget || !onChange) return;
    onChange({ number, holder, month, year, cvv }, validity);
  }, [number, holder, month, year, cvv, validity, onChange, embedWidget]);

  const cardNumberDisplay = useMemo(() => formatCardNumberDisplay(number.replace(/#/g, "•")), [number]);
  const expiryDisplay = `${month || "MM"}/${year ? year.slice(-2) : "YY"}`;
  const holderDisplay = truncateHolder(holder || "NAME ON CARD");

  const highlightClass = (() => {
    switch (focusField) {
      case "number":
        return "credit-card-form__highlight--number";
      case "holder":
        return "credit-card-form__highlight--holder";
      case "expire":
        return "credit-card-form__highlight--expire";
      case "cvv":
        return "credit-card-form__highlight--cvv";
      default:
        return "credit-card-form__highlight--hidden";
    }
  })();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit?.({ number, holder, month, year, cvv }, validity);
  };

  const payLabel =
    amount != null && amount > 0
      ? `Pay ${amount.toFixed(2)} ${currency.trim()} securely`
      : "Complete secure payment";

  const amountDisplay =
    amount != null && amount > 0
      ? amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : null;

  const cardPreview = (
    <div className={`credit-card-form__stage ${isCvvFocused ? "credit-card-form__stage--back" : ""}`}>
      <span className="credit-card-form__aurora credit-card-form__aurora--a" aria-hidden />
      <span className="credit-card-form__aurora credit-card-form__aurora--b" aria-hidden />
      <span className="credit-card-form__stage-grid" aria-hidden />

      {amountDisplay && (
        <div className="credit-card-form__amount">
          <span className="credit-card-form__amount-label">Amount due</span>
          <span className="credit-card-form__amount-value">
            {amountDisplay}
            <span className="credit-card-form__amount-currency">{currency.trim()}</span>
          </span>
        </div>
      )}

      <div
        ref={sceneRef}
        className="credit-card-form__card-scene"
        onPointerMove={handleSceneMove}
        onPointerLeave={handleSceneLeave}
      >
        <div className="credit-card-form__tilt">
          <div
            className={`credit-card-form__card ${isCvvFocused ? "credit-card-form__card--flip" : ""} ${
              reducedMotion ? "credit-card-form__card--reduced-motion" : ""
            }`}
          >
            <div className={`credit-card-form__highlight ${highlightClass}`} aria-hidden />

            <div className="credit-card-form__front">
              <span className="credit-card-form__pattern" aria-hidden />
              <span className="credit-card-form__glare" aria-hidden />
              <span className="credit-card-form__sheen" aria-hidden />
              <div className="credit-card-form__front-top">
                <div className="credit-card-form__brand-block">
                  <span className="credit-card-form__brand-name">{cardBrandLabel}</span>
                  <ContactlessOutlinedIcon className="credit-card-form__contactless" aria-hidden />
                </div>
                <CardNetworkLogo network={network} className="credit-card-form__network-logo" />
              </div>
              <div className="credit-card-form__chip" aria-hidden>
                <span />
              </div>
              <p className="credit-card-form__pan" aria-hidden>
                {cardNumberDisplay}
              </p>
              <div className="credit-card-form__front-bottom">
                <div className="credit-card-form__meta">
                  <span className="credit-card-form__meta-label">Card holder</span>
                  <span className="credit-card-form__meta-value">{holderDisplay}</span>
                </div>
                <div className="credit-card-form__meta credit-card-form__meta--expiry">
                  <span className="credit-card-form__meta-label">Expires</span>
                  <span className="credit-card-form__meta-value">{expiryDisplay}</span>
                </div>
              </div>
            </div>

            <div className="credit-card-form__back">
              <span className="credit-card-form__glare" aria-hidden />
              <div className="credit-card-form__stripe" aria-hidden />
              <div className="credit-card-form__signature-panel">
                <span className="credit-card-form__signature-line" aria-hidden />
                <div className="credit-card-form__cvv-panel">
                  <span className="credit-card-form__meta-label">CVV</span>
                  <span className="credit-card-form__cvv-value" aria-hidden>
                    {cvv ? "•".repeat(cvv.length) : "•••"}
                  </span>
                </div>
              </div>
              <div className="credit-card-form__back-footer">
                <span className="credit-card-form__back-brand">{cardBrandLabel}</span>
                <span className="credit-card-form__hologram" aria-hidden />
              </div>
            </div>
          </div>
        </div>
        <span className="credit-card-form__card-shadow" aria-hidden />
      </div>

      <p className="credit-card-form__stage-hint" aria-live="polite">
        {isCvvFocused
          ? "The 3-digit security code is on the back of your card"
          : "Your card preview updates as you type"}
      </p>

      <div className="credit-card-form__trust">
        <span className="credit-card-form__trust-chip">
          <LockOutlinedIcon fontSize="small" aria-hidden />
          256-bit encryption
        </span>
        <span className="credit-card-form__trust-chip">
          <VerifiedUserOutlinedIcon fontSize="small" aria-hidden />
          PCI-compliant processing
        </span>
      </div>
    </div>
  );

  const widgetForm = embedWidget ? (
    <>
      {widgetStatus === "loading" && (
        <div className="credit-card-form__widget-loading" role="status" aria-live="polite">
          <span className="credit-card-form__spinner" aria-hidden />
          Loading secure payment form…
        </div>
      )}

      {widgetStatus === "error" && (
        <div className="credit-card-form__widget-error" role="alert">
          <p>We couldn&apos;t load the secure payment form. Please try again.</p>
          <button type="button" className="credit-card-form__retry-btn" onClick={onWidgetError}>
            Retry
          </button>
        </div>
      )}

      <div
        ref={widgetRef}
        className={`credit-card-form__widget-body ${
          widgetStatus !== "ready" ? "credit-card-form__widget-body--pending" : ""
        }`}
      >
        {widgetSlot}
      </div>
    </>
  ) : null;

  const widgetFooter =
    embedWidget && widgetStatus === "ready" ? (
      <>
        <div className="credit-card-form__accepted" aria-label="Accepted card networks">
          <CardNetworkLogo network="visa" height={18} variant="dark" />
          <CardNetworkLogo network="mastercard" height={18} variant="dark" />
          <CardNetworkLogo network="amex" height={18} variant="dark" />
        </div>
        <p className="credit-card-form__trust-inline">
          Your payment information is encrypted and processed securely.
        </p>
      </>
    ) : null;

  return (
    <section className={`credit-card-form ${className}`.trim()} aria-label="Card payment">
      <div className="credit-card-form__shell">
        <div className="credit-card-form__layout">
          <aside className="credit-card-form__card-col">{cardPreview}</aside>

          <div className="credit-card-form__form-col">
            {embedWidget ? (
              <div className="credit-card-form__widget">
                <div className="credit-card-form__widget-head">
                  <div>
                    <p className="credit-card-form__widget-title">Card details</p>
                    <p className="credit-card-form__widget-sub">Enter the details exactly as they appear on your card.</p>
                  </div>
                  <span className="credit-card-form__secure-pill">
                    <LockOutlinedIcon aria-hidden />
                    Secure
                  </span>
                </div>
                {widgetForm}
                {widgetFooter}
              </div>
            ) : (
              <form className="credit-card-form__native-form" onSubmit={handleSubmit} noValidate>
                <div className="credit-card-form__field">
                  <label htmlFor="cc-holder">Name on card</label>
                  <input
                    id="cc-holder"
                    type="text"
                    autoComplete="cc-name"
                    placeholder="Name as shown on card"
                    value={holder}
                    onChange={(e) => setHolder(e.target.value.toUpperCase())}
                    onFocus={() => handleFocus("holder")}
                    onBlur={() => handleFocus(null)}
                  />
                </div>
                <div className="credit-card-form__field credit-card-form__field--number">
                  <label htmlFor="cc-number">Card number</label>
                  <div className="credit-card-form__input-with-icon">
                    <input
                      id="cc-number"
                      inputMode="numeric"
                      autoComplete="cc-number"
                      placeholder="1234 5678 9012 3456"
                      value={formatNumberSpaces(number.replace(/[^\d]/g, ""))}
                      onChange={(e) => setNumber(clampDigits(e.target.value, 19))}
                      onFocus={() => handleFocus("number")}
                      onBlur={() => handleFocus(null)}
                    />
                    <CardNetworkLogo network={network} height={20} variant="dark" />
                  </div>
                </div>
                <div className="credit-card-form__row-expiry-cvv">
                  <div className="credit-card-form__field">
                    <label htmlFor="cc-expiry">Expiry date</label>
                    <input
                      id="cc-expiry"
                      inputMode="numeric"
                      autoComplete="cc-exp"
                      placeholder="MM / YY"
                      value={month && year ? `${month}/${year.slice(-2)}` : month}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, "").slice(0, 4);
                        setMonth(digits.slice(0, 2));
                        const y = digits.slice(2, 4);
                        setYear(y ? `20${y}` : "");
                      }}
                      onFocus={() => handleFocus("expire")}
                      onBlur={() => handleFocus(null)}
                    />
                  </div>
                  <div className="credit-card-form__field">
                    <span className="credit-card-form__label-row">
                      <label htmlFor="cc-cvv">CVV</label>
                      <button
                        type="button"
                        className="credit-card-form__cvv-help"
                        aria-label="Where to find your security code"
                        title="The 3-digit code on the back of your card (4 digits on front for Amex)."
                      >
                        ?
                      </button>
                    </span>
                    <input
                      id="cc-cvv"
                      inputMode="numeric"
                      autoComplete="cc-csc"
                      placeholder="123"
                      value={cvv}
                      onChange={(e) => setCVV(clampDigits(e.target.value, 4))}
                      onFocus={() => handleFocus("cvv")}
                      onBlur={() => handleFocus(null)}
                    />
                  </div>
                </div>
                {showSubmit && (
                  <button className="credit-card-form__pay-btn" type="submit" disabled={!validity.allValid}>
                    {payLabel}
                  </button>
                )}
                <p className="credit-card-form__trust-inline">
                  Your payment information is encrypted and processed securely.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export { CreditCardForm };
